/**
 * Keep explanatory comments in this file as a reference for all other files in the drizzle folder.
 */
import { sql } from "drizzle-orm";
import { bigint, check, integer, pgEnum, pgTable, timestamp, uuid, varchar } from "drizzle-orm/pg-core";
import { bucketTypeKeyEnum, bucketTypes } from "./bucketTypes.js";
import { departmentEnum, taskChannelEnum, taskTypeKey, taskTypeKeyEnum } from "./taskTypes.js";
import { users } from "./users.js";
// No .references() — type safety without FK coupling; bucket task type concepts may diverge from task types over time
export const bucketAcceptedTaskTypeKey = {
    COST_ESTIMATE: taskTypeKey.COST_ESTIMATE,
    DAMAGE_APPRAISAL: taskTypeKey.DAMAGE_APPRAISAL,
    INVOICE: taskTypeKey.INVOICE,
    INBOX_PROCESSING: taskTypeKey.INBOX_PROCESSING,
    INCIDENT_REPORT: taskTypeKey.INCIDENT_REPORT,
};
// Bucket titles that carry an accepted task type and pull tasks. The CHECK below is built from it.
export const taskBucketTitle = {
    INBOX: "inbox",
    COST_ESTIMATE: "kostenvoranschlag",
    INVOICE: "rechnung",
};
const taskBucketTitleValues = new Set(Object.values(taskBucketTitle));
export function isTaskBucketTitle(title) {
    return taskBucketTitleValues.has(title);
}
export const bucketStatus = {
    PENDING: "pending",
    IN_PROGRESS: "in_progress",
    SUCCESS: "success",
    FAILED: "failed",
    CANCELLED: "cancelled",
    NEVER_STARTED: "never_started",
};
// Utility to convert enum to pgEnum tuple type
// https://gist.github.com/productdevbook/7c9ce3bbeb96b3fabc3c7c2aa2abc717#modern-enum-handling-and-type-safety
// Inlined here to avoid module resolution issues with drizzle-kit
function enumToPgEnum(myEnum) {
    return Object.values(myEnum);
}
// Define the task status enum as a PostgreSQL native enum type
// Export because Drizzle-Kit needs to see the enum definitions to generate proper CREATE TYPE SQL statements in migrations.
export const bucketStatusEnum = pgEnum("bucket_status", enumToPgEnum(bucketStatus));
// Export the enum values for type inference and use throughout the codebase
// export const taskStatusValues = taskStatusEnum.enumValues;
// https://gist.github.com/productdevbook/7c9ce3bbeb96b3fabc3c7c2aa2abc717
export const buckets = pgTable("bucket", {
    // GENERATED ALWAYS - strictly prevents manual insertion
    id: bigint({ mode: "number" }).primaryKey().generatedAlwaysAsIdentity(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .defaultNow()
        .notNull()
        .$onUpdate(() => new Date()),
    createdBy: uuid("created_by")
        .references(() => users.id)
        .notNull(),
    type: bucketTypeKeyEnum("type")
        .references(() => bucketTypes.key)
        .notNull(),
    title: varchar("title", { length: 100 }).notNull(),
    description: varchar("description", { length: 350 }),
    image: varchar("image", { length: 250 }),
    skillLevel: varchar("skill_level", { length: 100 }).notNull(),
    status: bucketStatusEnum("status").notNull(),
    targetQuantity: integer("target_quantity"),
    assignee: uuid("assignee")
        .references(() => users.id)
        .notNull(),
    plannedStartTime: timestamp("planned_start_time", { withTimezone: true }).notNull(),
    plannedEndTime: timestamp("planned_end_time", { withTimezone: true }).notNull(),
    startTime: timestamp("start_time", { withTimezone: true }),
    endTime: timestamp("end_time", { withTimezone: true }),
    duration: integer("duration").default(0),
    // No FK reference — see bucketAcceptedTaskTypeKey comment above
    acceptedTaskType: taskTypeKeyEnum("accepted_task_type"),
    // Null means any. Which values a task type allows is declared in allowedByTaskType.
    acceptedChannel: taskChannelEnum("accepted_channel"),
    acceptedDepartment: departmentEnum("accepted_department"),
}, (table) => [
    check("bucket_task_bucket_title_iff_accepted_task_type", 
    // drizzle-kit writes only the SQL text of a check into the migration snapshot. Bound parameters
    // would be lost, hence .inlineParams().
    sql `(${table.title} IN ${Object.values(taskBucketTitle)}) = (${table.acceptedTaskType} IS NOT NULL)`.inlineParams()),
]);
